import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, Menu, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FileNode, FileNodeTypeEnum, FilePermissionType } from '../../api-client/autogenerated';
import ManagePermissionsDialog, { ManagePermissionsDialogType } from './ManagePermissionsDialog';
import CircularLoader from '../loader/CircularLoader';
import { areFilesInSameParentFolder, isPublicPage, isUserInGroup } from '../../scripts/utils';
import {
  getDirectDesignFileDownloadLink,
  getDownloadLinkForTrashItem,
  sendDirectDesignFileDownloadEmails,
  sendPublicFolderAccessToEmails,
  sendPublicFolderAccessToEveryone,
} from '../../models/api/filesystem';
import { useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';
import FileLogDialog from './FileLogDialog';
import EmailUsersDialog from './EmailUsersDialog';
import { getUserState } from '../../features/user/selectors';
import { getGroupsState } from '../../features/groups/selector';
import { DialogKey, useDialogState } from './context/DialogStateContext';

interface FileOptionsDialogProps {
  dialogKey: DialogKey;
  handleClose: () => void;
  anchor: HTMLElement | null;
  anchorPoint?: { top: number; left: number } | null;
  file: FileNode | undefined;
  setFile: React.Dispatch<React.SetStateAction<FileNode | undefined>> | ((f: FileNode) => void);
  renameClicked: () => void;
  deleteClicked: () => void;
  disableShare?: boolean;
  disableRename?: boolean;
  disableDelete?: boolean;
  updateFileNode?: (node: FileNode) => void;
  openUploadFileDialog?: () => void;
  openCreateFolderDialog?: () => void;
  disableAddFile?: boolean;
  disableAddFolder?: boolean;
  viewFile?: () => void;
  downloadFile?: () => Promise<void>;
  shareButtonTrigger?: boolean | undefined;
  findParentFolder: (fullKey: string) => FileNode | undefined;
  manageParentFolder: (fullKey: string) => void;
  selectedNodes: FileNode[];
  isRecycleBin?: boolean;
  openManagePermissionsDialog?: boolean;
}

const useStyles = makeStyles({
  popover: {
    '.rootElement > *': {
      maxHeight: '5000px',
    },
  },
  popoverRoot: {
    pointerEvents: 'none',
    outline: 'none',
  },
  paper: {
    pointerEvents: 'all',
  },
  paper2: {
    pointerEvents: 'all',
    minWidth: 290,
  },
  menuItem: {
    width: 160,
    height: 32,
  },
  spaceBetween: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  menuItemActive: {
    width: 140,
    height: 32,
    backgroundColor: '#E6EEFF',
  },
  checkbox: {
    padding: '0px 6px 0px 6px',
  },
});

export default function FileOptionsDialog(props: FileOptionsDialogProps) {
  const {
    dialogKey,
    handleClose,
    anchor,
    anchorPoint,
    file,
    renameClicked,
    setFile,
    deleteClicked,
    disableShare,
    disableRename,
    disableDelete,
    updateFileNode,
    openUploadFileDialog,
    openCreateFolderDialog,
    disableAddFile,
    disableAddFolder,
    shareButtonTrigger,
    viewFile,
    downloadFile,
    findParentFolder,
    manageParentFolder,
    selectedNodes,
    isRecycleBin,
    openManagePermissionsDialog,
  } = props;

  const user = useSelector(getUserState);
  const project = useSelector(getProjectState);
  const groups = useSelector(getGroupsState);

  const [managePermissionsDialogOpen, setManagePermissionsDialogOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [getALinkDialogOpen, setGetALinkDialogOpen] = useState(false);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [notifyUsersDialogOpen, setNotifyUsersDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);

  const classes = useStyles();

  const isPublic = isPublicPage();

  const isRootNode = file?.fullKey === '/';

  const canNotify = areFilesInSameParentFolder(selectedNodes);

  const { isOpen, close } = useDialogState();
  const open = isOpen(dialogKey);

  const hasAdminPermission = file?.fileNodePermissions?.some((p) => {
    if (!p.permissions.includes(FilePermissionType.Delete)) return false;
    if (p.shareToEveryone) return true;
    if (p.userIdTarget === user.id) return true;
    const groupTarget = groups.find((g) => g.id === p.userGroupIdTarget);
    if (groupTarget) {
      return isUserInGroup(groupTarget, user.id);
    }
    return false;
  });

  const hasModifyPermission = file?.fileNodePermissions?.some((p) => {
    if (!p.permissions.includes(FilePermissionType.Modify)) return false;
    if (p.shareToEveryone) return true;
    if (p.userIdTarget === user.id) return true;
    const groupTarget = groups.find((g) => g.id === p.userGroupIdTarget);
    if (groupTarget) {
      return isUserInGroup(groupTarget, user.id);
    }
    return false;
  });

  // The API lets the owner of a filesystem rename and delete anything in it without a share of its
  // own, so a node of theirs that carries no shares is still theirs to manage. Without this, a
  // folder that never got shared - which is how a folder created with a "/" in its name ended up -
  // has no Rename or Delete at all.
  const isOwnedByUser = !!file && !isRootNode && file.ownerId === user.id;

  // at the top with other useState imports
  const [frozenAnchorPoint, setFrozenAnchorPoint] = useState<{ top: number; left: number } | null>(
    null,
  );

  useEffect(() => {
    if (shareButtonTrigger !== undefined) {
      setManagePermissionsDialogOpen(true);
    }
  }, [shareButtonTrigger]);

  const closeMenu = () => {
    handleClose();
  };

  useEffect(() => {
    if (openManagePermissionsDialog) {
      setManagePermissionsDialogOpen(true);
    }
  }, [openManagePermissionsDialog]);

  function handleContextMenu(event: any) {
    event.stopPropagation();
    event.preventDefault();
    handleClose();
  }

  const handleRename = () => {
    setRenameDialogOpen(true);
    close(dialogKey);
  };

  const handleView = () => {
    viewFile && viewFile();
    closeMenu();
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      downloadFile && (await downloadFile());
    } finally {
      setDownloading(false);
      closeMenu();
    }
  };

  const handleDelete = () => {
    deleteClicked();
    closeMenu();
  };

  const handleGetLink = async (): Promise<string> => {
    if (!file || !project) return '';
    if (isRecycleBin) return getDownloadLinkForTrashItem(file.fullKey);
    return getDirectDesignFileDownloadLink(project.id, file.fullKey, file.ownerId).then(
      (result) => result.data.downloadLink,
    );
  };

  const handleFolderGetLink = async (): Promise<string> => {
    if (!file || !project) return '';
    try {
      const res = await sendPublicFolderAccessToEveryone(project.id, file.fullKey, file.ownerId);
      return res.data;
    } catch (e: any) {
      return '';
    }
  };

  const submitEmails = async (emails: string[]) => {
    if (!file || !project) return;
    if (file?.type === FileNodeTypeEnum.File)
      await sendDirectDesignFileDownloadEmails(project.id, file.fullKey, file.ownerId, emails);
    else await sendPublicFolderAccessToEmails(project.id, file.fullKey, file.ownerId, emails);
    handleClose();
  };

  const getSafePoint = () => {
    // prefer an explicit anchorPoint if it's valid
    if (
      anchorPoint &&
      typeof (anchorPoint as any).top === 'number' &&
      typeof (anchorPoint as any).left === 'number'
    ) {
      return { top: (anchorPoint as any).top, left: (anchorPoint as any).left };
    }

    // otherwise, derive from the anchor element if available
    if (anchor && typeof anchor.getBoundingClientRect === 'function') {
      const r = anchor.getBoundingClientRect();
      return { top: r.bottom, left: r.left };
    }

    // nothing usable
    return null;
  };

  useEffect(() => {
    if (open) {
      const current = getSafePoint();
      if (current) {
        setFrozenAnchorPoint(current);
      } else {
        // no valid anchor — don't open/jump
        setFrozenAnchorPoint(null);
      }
    } else {
      setFrozenAnchorPoint(null);
    }
  }, [open, anchor, anchorPoint]);

  return (
    <>
      <Menu
        onContextMenu={handleContextMenu}
        classes={{ paper: classes.paper }}
        elevation={2}
        open={open && Boolean(frozenAnchorPoint)}
        onClose={() => close(dialogKey)}
        keepMounted
        getContentAnchorEl={null}
        anchorReference="anchorPosition"
        anchorPosition={frozenAnchorPoint ?? undefined}
        anchorEl={undefined}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {!disableAddFile && !isPublic && !isRecycleBin ? (
          <MenuItem
            className={classes.menuItem}
            onClick={() => {
              openUploadFileDialog && openUploadFileDialog();
              handleClose();
            }}
          >
            New File
          </MenuItem>
        ) : null}
        {!disableAddFolder && !isPublic && !isRecycleBin ? (
          <MenuItem
            className={classes.menuItem}
            onClick={() => {
              openCreateFolderDialog && openCreateFolderDialog();
              handleClose();
            }}
          >
            New Folder
          </MenuItem>
        ) : null}
        {viewFile ? (
          <MenuItem className={classes.menuItem} onClick={handleView}>
            View
          </MenuItem>
        ) : null}
        {downloadFile ? (
          <MenuItem className={classes.menuItem} onClick={handleDownload}>
            Download
            {downloading && <CircularLoader size={25} style={{ marginLeft: 8 }} />}
          </MenuItem>
        ) : null}
        {!isPublic && !isRootNode ? (
          <MenuItem className={classes.menuItem} onClick={() => setGetALinkDialogOpen(true)}>
            Send A Link
          </MenuItem>
        ) : null}
        {!disableShare && !isPublic && !isRecycleBin ? (
          <MenuItem
            className={classes.menuItem}
            onClick={() => setManagePermissionsDialogOpen(true)}
          >
            Define Access
          </MenuItem>
        ) : null}
        {!isPublic && !isRootNode && canNotify && !isRecycleBin ? (
          <MenuItem className={classes.menuItem} onClick={() => setNotifyUsersDialogOpen(true)}>
            Notify
          </MenuItem>
        ) : null}
        {!isPublic && !isRootNode ? (
          <MenuItem className={classes.menuItem} onClick={() => setLogDialogOpen(true)}>
            View Activity
          </MenuItem>
        ) : null}
        {!disableRename && !isPublic && !isRecycleBin && (hasModifyPermission || isOwnedByUser) ? (
          <MenuItem className={classes.menuItem} onClick={handleRename}>
            Rename
          </MenuItem>
        ) : null}
        {!disableDelete && !isPublic && (hasAdminPermission || (!isRecycleBin && isOwnedByUser)) ? (
          <MenuItem
            className={classes.menuItem}
            style={{ color: '#ED3F26' }}
            onClick={handleDelete}
          >
            Delete
          </MenuItem>
        ) : null}
      </Menu>
      <ManagePermissionsDialog
        dialogOpen={managePermissionsDialogOpen}
        type={ManagePermissionsDialogType.Design}
        closeDialog={() => {
          setManagePermissionsDialogOpen(false);
          handleClose();
        }}
        files={file ? [file!] : undefined}
        setFile={(node: FileNode) => {
          setFile(node);
          if (updateFileNode) updateFileNode(node);
        }}
        findParentFolder={findParentFolder}
        manageParentFolder={(fullKey: string) => {
          handleClose();
          manageParentFolder(fullKey);
        }}
      />
      <EmailUsersDialog
        dialogOpen={getALinkDialogOpen}
        closeDialog={() => setGetALinkDialogOpen(false)}
        onSubmit={submitEmails}
        getLink={file?.type === FileNodeTypeEnum.File ? handleGetLink : handleFolderGetLink}
        defaultSubject={
          file?.type === FileNodeTypeEnum.File
            ? `New Direct Download Share in ${project?.name}`
            : `New Folder Share in ${project?.name}`
        }
      />
      {file ? (
        <FileLogDialog
          open={logDialogOpen}
          handleClose={() => setLogDialogOpen(false)}
          file={file}
          isRecycleBin={isRecycleBin}
        />
      ) : null}
      {file ? (
        <EmailUsersDialog
          dialogOpen={notifyUsersDialogOpen}
          files={selectedNodes.length > 0 ? selectedNodes : [file]}
          closeDialog={() => setNotifyUsersDialogOpen(false)}
          isDesignNotify
        />
      ) : null}
      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogContent style={{ textAlign: 'center', maxWidth: 350 }}>
          Caution: renaming the file will invalidate all existing links to this file
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)} variant="text" color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              renameClicked();
              setRenameDialogOpen(false);
            }}
            variant="text"
            color="primary"
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
